import React, { useState, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import CloseIcon from "@mui/icons-material/Close";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckIcon from "@mui/icons-material/Check";
import Dialog from "@mui/material/Dialog";

import BuyUnits from "./Buyunit";
import Apa1 from "../../Assets/images/apa1.png";

import ActiveFinancedDetailsTab from "./ActiveFinancedDetailsTab";
import ActiveFinancedTransactionsTab from "./ActiveFinancedTransactionsTab";
import ActiveFinancedProgressTab from "./ActiveFinancedProgressTab";

import getServices from "../../Services/get-service";

const drawerWidth = 490;

function ActiveFinancedDrawer({ open, handleClose, details: rawDetails }) {
  const details = rawDetails || {};
  const [activeTab, setActiveTab] = useState(0);
  const [buyUnitsOpen, setBuyUnitsOpen] = useState(false);
  const [selectedPreviewImage, setSelectedPreviewImage] = useState(null);

  const [projectPhases, setProjectPhases] = useState([]);

  const [transactionsList, setTransactionsList] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveTab(details?.initialTab !== undefined ? details.initialTab : 0);
    }
  }, [open, details]);

  useEffect(() => {
    if (open && details?.id) {
      getServices.getProjectPhases(details.id)
        .then(response => {
          if (response?.data?.statusCode === 200) {
            setProjectPhases(response.data.data || []);
          } else {
            setProjectPhases([]);
          }
        })
        .catch(err => {
          console.error("Error fetching project phases", err);
          setProjectPhases([]);
        });
    }
  }, [open, details?.id]);

  useEffect(() => {
    if (open && details?.walletId) {
      setLoadingTransactions(true);
      getServices.getActiveProjectTransactions(details.walletId)
        .then(response => {
          if (response?.data?.statusCode === 200) {
            const data = response.data.data;
            if (Array.isArray(data)) {
              setTransactionsList(data);
            } else if (data && Array.isArray(data.content)) {
              setTransactionsList(data.content);
            } else if (data && Array.isArray(data.transactions)) {
              setTransactionsList(data.transactions);
            } else {
              setTransactionsList([]);
            }
          } else {
            setTransactionsList([]);
          }
        })
        .catch(err => {
          console.error("Error fetching transactions", err);
          setTransactionsList([]);
        })
        .finally(() => {
          setLoadingTransactions(false);
        });
    } else {
      setTransactionsList([]);
    }
  }, [open, details?.walletId]);

  const title = details.title || "";
  const type = details.type || details.propertyType || "Land";
  const isLand = type.toLowerCase() === "land";
  const location =
    details.location ||
    details.locality ||
    "";

  let displayImage = Apa1;
  if (details.image) {
    displayImage = details.image;
  } else if (
    Array.isArray(details.projectImages) &&
    details.projectImages.length > 0
  ) {
    displayImage =
      details.projectImages[0]?.imageUrl || details.projectImages[0];
  } else if (Array.isArray(details.imageUrl) && details.imageUrl.length > 0) {
    displayImage = details.imageUrl[0];
  } else if (typeof details.imageUrl === "string" && details.imageUrl) {
    displayImage = details.imageUrl;
  }

  const unitsHeld = details.unitsHeld ?? details.unitsFinanced ?? 0;
  const pricePerUnit = details.pricePerUnit ?? 0;
  const amountFinanced =
    details.amountFinanced ?? 0
  const dateFinanced = details.dateFinanced || "";
  const certificateDate =
    details.certificateDate || dateFinanced || "";
  const developerName = details.developerName || "";
  const unitsLeft = details.unitsLeft ?? details.plotsAvailable ?? 0;

  // Transactions list
  const transactions = transactionsList.length > 0 
    ? transactionsList 
    : (Array.isArray(details.transactions) ? details.transactions : []);

  // Project Progress data for Updates tab
  const rawProgress = details.progress || details.updates;
  const stages = projectPhases.length > 0 ? projectPhases : (Array.isArray(rawProgress)
    ? rawProgress
    : Array.isArray(rawProgress?.stages)
      ? rawProgress.stages
      : Array.isArray(details.stages)
        ? details.stages
        : []);
  const lastUpdated = rawProgress?.lastUpdated || details.lastUpdated;

  const formatAmount = (val) => {
    if (val === undefined || val === null) return "0";
    return Number(val).toLocaleString("en-US");
  };

  const handleOpenFinance = () => {
    setBuyUnitsOpen(true);
  };

  const handleCloseFinance = () => {
    setBuyUnitsOpen(false);
  };

  return (
    <>
      {/* Finance unit Modal */}
      <BuyUnits
        open={buyUnitsOpen}
        handleClose={handleCloseFinance}
        closeDrawer={handleClose}
        number={unitsLeft}
        id={details.id}
        amount={pricePerUnit}
        type={isLand ? "land" : "property"}
        details={details}
      />
      <Drawer
        anchor="right"
        variant="temporary"
        open={open}
        onClose={handleClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: { xs: "100%", sm: drawerWidth },
            backgroundColor: "#FFFFFF",
            boxShadow: "-8px 0 25px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <div className="flex flex-col h-full bg-[#FFFFFF] text-[#1E222B]">
          <div className="flex-1 overflow-y-auto pb-6">
            <div className="relative h-[255px] md:h-[270px] w-full overflow-hidden flex-shrink-0">
              <img
                src={displayImage}
                alt={title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/85 pointer-events-none" />
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-9 h-9 rounded-full bg-black/55 hover:bg-black/80 backdrop-blur-xs flex items-center justify-center text-white cursor-pointer transition-transform active:scale-95 shadow-sm"
                  aria-label="Close drawer"
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </button>
              </div>

              <div className="absolute bottom-4 left-4 right-4 z-10">
                <span className={`inline-block px-3 py-1 rounded-[6px] text-white text-[11px] font-bold shadow-xs mb-1.5 ${isLand ? "bg-[#76D400]" : "bg-[#0088FF]"}`}>
                  {type}
                </span>
                <h2 className="text-white font-[800] text-[22px] md:text-[24px] tracking-tight leading-tight drop-shadow-xs">
                  {title}
                </h2>
                <div className="flex items-center gap-1 text-white/95 text-[12px] mt-1 drop-shadow-2xs">
                  <PlaceOutlinedIcon sx={{ fontSize: 15, color: "#FFFFFF" }} />
                  <span className="font-[400] truncate">{location}</span>
                </div>
              </div>
            </div>
            <div className="px-5 pt-4 pb-2 bg-white">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[#808285] text-[12px] font-[500]">
                    Amount Financed
                  </p>
                  <p className="text-[#1E222B] font-[700] text-[26px] md:text-[28px] tracking-tight leading-tight mt-1">
                    ₦{formatAmount(amountFinanced)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[#808285] text-[12px] font-[500]">Units</p>
                  <p className="text-[#1E222B] font-[800] text-[18px] md:text-[20px] leading-tight mt-1">
                    {unitsHeld}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 mt-2">
              <button
                type="button"
                onClick={handleOpenFinance}
                className="w-full bg-[#00BC67] hover:bg-[#00a85c] text-white font-[700] text-[14px] py-3.5 rounded-[10px] transition-all shadow-xs cursor-pointer active:scale-[0.99]"
              >
                Finance more units
              </button>
              <div className="mt-3 bg-[#F5F7FA] border border-[#E1F3E9] rounded-[10px] p-3 flex items-start gap-2.5">
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 18,
                    color: "#00BC67",
                    flexShrink: 0,
                    mt: 0.2,
                  }}
                />
                <p className="text-[#00BC67] text-[12px] font-[500] leading-relaxed">
                  Your certificate of ownership was issued on {certificateDate}.
                  You'll be notified as construction milestones are logged.
                </p>
              </div>
            </div>
            <div className="mt-5">
              <div className="flex items-center border-b border-[#F1F5F9] bg-white px-5">
                <button
                  type="button"
                  onClick={() => setActiveTab(0)}
                  className={`flex-1 py-3 text-center text-[13px] font-[700] transition-colors relative cursor-pointer ${activeTab === 0
                    ? "text-[#1E222B]"
                    : "text-[#808285] hover:text-[#1E222B]"
                    }`}
                >
                  Details
                  {activeTab === 0 && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[2.5px] bg-[#00BC67] rounded-full" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab(1)}
                  className={`flex-1 py-3 text-center text-[13px] font-[700] transition-colors relative cursor-pointer ${activeTab === 1
                    ? "text-[#1E222B]"
                    : "text-[#808285] hover:text-[#1E222B]"
                    }`}
                >
                  Transactions
                  {activeTab === 1 && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[2.5px] bg-[#00BC67] rounded-full" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab(2)}
                  className={`flex-1 py-3 text-center text-[13px] font-[700] transition-colors relative cursor-pointer ${activeTab === 2
                    ? "text-[#1E222B]"
                    : "text-[#808285] hover:text-[#1E222B]"
                    }`}
                >
                  Updates
                  {activeTab === 2 && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[2.5px] bg-[#00BC67] rounded-full" />
                  )}
                </button>
              </div>

              <div className="p-5">
                {activeTab === 0 && (
                  <ActiveFinancedDetailsTab
                    unitsHeld={unitsHeld}
                    pricePerUnit={pricePerUnit}
                    amountFinanced={amountFinanced}
                    dateFinanced={dateFinanced}
                    developerName={developerName}
                    formatAmount={formatAmount}
                  />
                )}

                {activeTab === 1 && (
                  <ActiveFinancedTransactionsTab
                    loadingTransactions={loadingTransactions}
                    transactions={transactions}
                    formatAmount={formatAmount}
                    amountFinanced={amountFinanced}
                    dateFinanced={dateFinanced}
                  />
                )}

                {activeTab === 2 && (
                  <ActiveFinancedProgressTab
                    stages={stages}
                    lastUpdated={lastUpdated}
                    setSelectedPreviewImage={setSelectedPreviewImage}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Drawer>

      {/* Progress Image Lightbox Modal */}
      <Dialog
        open={Boolean(selectedPreviewImage)}
        onClose={() => setSelectedPreviewImage(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <div className="relative p-2 bg-black/85 backdrop-blur-md rounded-[16px] flex flex-col items-center">
          <button
            type="button"
            onClick={() => setSelectedPreviewImage(null)}
            className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-black/60 hover:bg-black/90 text-white flex items-center justify-center cursor-pointer transition-colors shadow-sm"
            aria-label="Close preview"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
          <img
            src={selectedPreviewImage}
            alt="Construction progress preview"
            className="w-full h-auto max-h-[75vh] object-contain rounded-[10px]"
          />
        </div>
      </Dialog>
    </>
  );
}
export default ActiveFinancedDrawer;
